import { useParams, useSearchParams } from "react-router-dom";
import { Box } from "@mui/material";
import { Loading, LoadingFailedPage } from "~/components/loading";
import { Space } from "~/lib/types";
import {
  AggregateScoreType,
  CaseStatus,
  DocumentType,
  ExceptionType,
  FindingType,
  GetAffectedAssetsDocument,
  GetAggregateScoresDocument,
  GetCasesCountDocument,
  ScoreRating,
  UiPage,
  ScoreState,
  ScoreStateFilter,
  useGetAggregateScoresQuery,
  useGetCasesCountQuery,
  useLoadAdvisoryQuery,
} from "~/operations";
import {
  CveRiskFactors,
  Sources,
  SourcesProps,
} from "~/components/vulnerabilities";
import { AdvisoryContentDetails } from "~/components/vulnerabilities/advisory-content-details";
import { useInventory } from "~/pages/inventory/hooks";
import { FlagOutlined, Radar } from "@mui/icons-material";
import { SectionHeading } from "~/components/DetailsPage/components";
import { StatsProps } from "~/components/DetailsPage/components/Stats/Stats";
import { useFindingRiskFactors } from "~/pages/space/security/components/RiskFactors/hooks/useFindingRiskFactors";
import { useTickets } from "~/pages/tickets/hooks";
import { ScrollToTop } from "~/lib/scroll-to-top";
import {
  ExposedAssets,
  ExposedAssetsAdvisoryNode,
  ExposedAssetsCVENode,
  AssetContextualLinkType,
  SelectedEntityWithExposedAssetData,
} from "~/pages/space/vulnerabilities/components/exposed-assets";
import { ScopeType, SpaceOrWorkspaceScope } from "~/hooks/useScope";
import { useReportViewed } from "~/hooks/useReportViewed";
import { ChangeEvent, useEffect } from "react";
import { DetailPageTopSection } from "~/components/DetailPageLayouts/DetailPageTopSection";
import { GenerateReportButton } from "~/components/GenerateReport/GenerateReportButton";
import { InventoryPageHeader } from "~/routes";
import { useExceptions } from "~/components/exceptions/use-exceptions";
import { setDocumentTitle } from "~/lib/commonUtils";
import { getExceptionLink } from "~/pages/findings/Exceptions/utils";
import { TicketsSummary } from "~/pages/tickets/components/TicketsSummary/TicketsSummary";
import { ExposedAsset } from "~/pages/space/vulnerabilities/components/exposed-assets/types";
import { Remediation } from "~/components/Remediation/Remediation";
import { useDialogs } from "@toolpad/core";
import { RemoveExceptionsGroupDialog } from "~/components/exceptions/RemoveExceptionsGroupDialog";
import {
  SelectedEntityWithFindingData,
  SetExceptionDialog,
} from "~/components/exceptions/SetExceptionDialog";
import { CreateTicketDialog } from "~/pages/tickets/components/CreateTicketDialog";
import { useFeatureFlags } from "~/providers/FeatureFlagsProvider";
import { useSelection } from "~/hooks/useSelection";
import { useTicketingIntegrations } from "~/pages/tickets/hooks/useTicketingIntegrations";
import { SelectedEntityWithTicketData } from "~/pages/tickets/types";
import { SCORE_STATE_FILTER_KEY } from "~/pages/inventory/asset/useAssetVulnerabilitiesFilterContext";
import { useSecurityPipelineIntegrations } from "~/pages/integrations/security-pipeline/hooks/useSecurityPipelineIntegrations";
import { CreateSecurityPipelinePrDialog } from "~/pages/integrations/security-pipeline/components/CreatePullRequest/CreateSecurityPipelinePRDialog";
import { SelectedEntityWithException } from "~/components/exceptions/types";
import { flattenExposedAssetsFindings } from "~/pages/integrations/security-pipeline/components/CreatePullRequest/utils/flattenExposedAssetsFindings";
import { ScoreBlock } from "~/pages/space/security/components/Check/ScoreBlock";

interface SpaceAdvisoryPageProps {
  space: Space;
  scope: SpaceOrWorkspaceScope;
}

export const SpaceAdvisoryPage = ({ space, scope }: SpaceAdvisoryPageProps) => {
  const { id = "" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isFeatureEnabled } = useFeatureFlags();
  const vulnId = searchParams.get("vulnId");
  const { assetsTotalCount } = useInventory({ scope });
  const dialogs = useDialogs();
  const {
    data,
    loading: advisoryLoading,
    error,
  } = useLoadAdvisoryQuery({
    variables: { id, spaceMrn: space.mrn },
  });

  useReportViewed({
    page: UiPage.Finding,
    pageLoaded: !advisoryLoading && data !== undefined,
    assetMrn: data?.advisory?.mrn,
    tab: "advisory",
  });

  const { data: aggScoreData, loading: aggScoreLoading } =
    useGetAggregateScoresQuery({
      variables: {
        entityMrn: scope.mrn || "",
        filter: {
          findingMrn: data?.advisory?.mrn,
        },
      },
      skip: !data?.advisory?.mrn || !scope.mrn,
    });

  const { riskFactorsWithDocs, loading: riskFactorsLoading } =
    useFindingRiskFactors({
      spaceMrn: space.mrn,
      findingMrn: String(data?.advisory?.mrn || ""),
      scoreType: AggregateScoreType.Advisory,
      onlyAffected: false,
    });

  useEffect(() => {
    searchParams.set(SCORE_STATE_FILTER_KEY, ScoreState.Open);
    setSearchParams(searchParams, { replace: true });
  }, []);

  const {
    handleNodeClick,
    handleCancelClick,
    selectedEntities: selectedAffectedAssets,
    setSelectedEntities: setSelectedAffectedAssets,
  } = useSelection<
    SelectedEntityWithExposedAssetData &
      SelectedEntityWithException &
      SelectedEntityWithFindingData
  >();

  const {
    handleSetExceptionForScopes,
    handleRemoveExceptionGroups,
    loading: exceptionsLoading,
  } = useExceptions({
    onSetException: () => {
      setSelectedAffectedAssets([]);
    },
    multipleScopes: true,
    scopeMrns:
      selectedAffectedAssets.length > 0
        ? selectedAffectedAssets.map((s) => s.scopeMrn)
        : [scope.mrn],
    advisoryMrns: [data?.advisory?.mrn || ""],
    applyToCves: true,
    refetchQueries: [GetAggregateScoresDocument, GetAffectedAssetsDocument],
  });

  const {
    handleCreateCase,
    handleCreateTicketingIntegrationModalOpen,
    hasCreateTicketPermissions,
    hasListTicketsPermissions,
    loading: createTicketLoading,
  } = useTickets({
    createCaseRefetchQueries: [GetCasesCountDocument],
    onTicketCreate: () => setSelectedAffectedAssets([]),
  });

  const { data: tickets } = useGetCasesCountQuery({
    variables: {
      input: {
        scopeMrn: scope.mrn,
        findingMrns: [data?.advisory?.mrn as string],
        statuses: [CaseStatus.Open],
      },
    },
    fetchPolicy: "cache-and-network",
    skip: !data?.advisory?.mrn || !hasListTicketsPermissions,
  });

  const { hasTicketingIntegration } = useTicketingIntegrations();
  const { hasSecurityPipelineIntegrations } = useSecurityPipelineIntegrations();

  const advisory = data?.advisory;
  const aggScoreEdges =
    aggScoreData?.aggregateScores?.__typename === "AggregateScoresConnection"
      ? aggScoreData.aggregateScores.edges
      : [];
  const aggScore =
    aggScoreData?.aggregateScores?.__typename === "AggregateScoresConnection"
      ? aggScoreEdges?.at(0)?.node
      : undefined;
  // When no aggregateScore exists, we fallback cvssScore of the Advisory.
  const cvssScore = aggScore?.cvss || advisory?.cvssScore;

  const loading = advisoryLoading || aggScoreLoading || riskFactorsLoading;

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          width: "100%",
        }}
      >
        <Loading what="Advisory" />
      </Box>
    );
  }

  if (error || !advisory) {
    return <LoadingFailedPage what="Advisory" />;
  }

  const sourceLinks: SourcesProps["links"] = (advisory?.externalUrls || [])
    .flatMap((link) => link ?? [])
    .map((reference) => ({
      href: reference.url,
      text: reference.title || "Advisory Source Link",
      icon: reference.iconIDEnum,
    }));

  const totalScanned = assetsTotalCount;
  const totalAffected = aggScore?.blastRadius?.affected || 0;

  const stats: StatsProps["stats"] = [
    {
      label: "Scanned",
      value: totalScanned < 0 ? "---" : totalScanned.toString(),
      icon: <Radar fontSize="inherit" />,
    },
    {
      label: "Exposed",
      value: totalAffected < 0 ? "---" : totalAffected.toString(),
      icon: <FlagOutlined fontSize="inherit" />,
      onClick: () => {
        document
          .querySelector(`#affected-assets`)
          ?.scrollIntoView({ behavior: "smooth" });
      },
    },
    // {
    //   label: "Updated",
    //   count: -1, // TODO
    //   icon: <ZoomOutMap fontSize="inherit" />,
    // },
  ];

  const handleCreateTicketDialogOpen = ({
    selectedAssets,
    isScopeLevelTicket,
  }: {
    selectedAssets: SelectedEntityWithExposedAssetData[];
    isScopeLevelTicket: boolean;
  }) => {
    dialogs.open(CreateTicketDialog, {
      isCombinedTicket: true,
      target: "asset",
      loading: loading || createTicketLoading,
      space: space,
      scope: scope,
      hasCreateCasePermissions: hasCreateTicketPermissions,
      onSave: (data) =>
        handleCreateCase({
          data,
          refetchQueries: [GetAggregateScoresDocument],
        }),
      selectedEntities: selectedAssets.map((s) => ({
        ...s,
        mrn: advisory.mrn,
      })),
      assetsCount: isScopeLevelTicket ? totalAffected : selectedAssets.length,
    });
  };

  const handleSecurityPipelinePRDialogOpen = () => {
    dialogs.open(CreateSecurityPipelinePrDialog, {
      aggregateScoresEdges: aggScoreEdges,
      spaceScope: space,
    });
  };

  const handleFindingsSecurityPipelinePRDialogOpen = ({
    selectedAssets,
    findings,
  }: {
    selectedAssets: SelectedEntityWithTicketData[];
    findings: Array<ExposedAssetsAdvisoryNode | ExposedAssetsCVENode>;
  }) => {
    dialogs.open(CreateSecurityPipelinePrDialog, {
      findings: flattenExposedAssetsFindings({ findings }),
      spaceScope: space,
      assetMrns: selectedAssets.map((s) => s.scopeMrn),
    });
  };

  const handleSetExceptionDialogOpen = ({
    selectedAssets,
  }: {
    selectedAssets: Array<
      SelectedEntityWithExposedAssetData &
        SelectedEntityWithException &
        SelectedEntityWithFindingData
    >;
  }) => {
    dialogs.open(SetExceptionDialog, {
      target: "advisory",
      selectedEntities: selectedAssets,
      targetData: {
        targetTitle: advisory.id || "",
      },
      onSave: handleSetExceptionForScopes,
    });
  };

  const handleRemoveExceptionDialogOpen = ({
    selectedAssets,
  }: {
    selectedAssets: Array<
      SelectedEntityWithExposedAssetData &
        SelectedEntityWithException &
        SelectedEntityWithFindingData
    >;
  }) => {
    dialogs.open(RemoveExceptionsGroupDialog, {
      onRemove: async (params) => {
        await handleRemoveExceptionGroups(params);
        setSelectedAffectedAssets([]);
      },
      exceptionMrns: selectedAssets
        .map((a) => a.exception?.exceptionMrn)
        .filter((mrn): mrn is string => Boolean(mrn)),
    });
  };

  setDocumentTitle([advisory.title, "Advisories"]);

  return (
    <>
      <ScrollToTop />
      <DetailPageTopSection
        content={{
          triageBar: {
            title: advisory.id || `Advisory ${advisory.id}`,
            fallbackPath: vulnId
              ? `/space/vulns/cve/${vulnId}?${scope.params}`
              : `/space/findings/advisories?${scope.params}`,
            triage: true,
            subheaders: [
              {
                id: "created",
                label: "Created at",
                value: String(advisory.publishedAt),
              },
              {
                id: "last-modified",
                label: "Last modified",
                value: String(advisory.modifiedAt),
              },
            ],
            toolbarItems: [
              {
                id: "generate-report",
                component: (
                  <GenerateReportButton
                    documentType={DocumentType.AdvisoryReport}
                    title={advisory.title || "Generated Advisory Report"}
                    space={space}
                    advisoryId={advisory.id}
                    IconProps={{ sx: { color: "text.primary" } }}
                  />
                ),
              },
            ],
            isLoading: loading || exceptionsLoading || createTicketLoading,
            availableRemediations: {
              ticket: {
                active: true,
                onClick: hasTicketingIntegration
                  ? () =>
                      handleCreateTicketDialogOpen({
                        selectedAssets: [
                          {
                            mrn: advisory?.mrn,
                            scopeMrn: scope.mrn,
                            findingMrn: advisory?.mrn || "",
                          },
                        ],
                        isScopeLevelTicket: true,
                      })
                  : handleCreateTicketingIntegrationModalOpen,
              },
              securityPipeline: {
                active:
                  isFeatureEnabled("Security Pipeline") &&
                  hasSecurityPipelineIntegrations,
                onClick: handleSecurityPipelinePRDialogOpen,
              },
              exception: {
                active: scope.type === ScopeType.Space,
                hasException: !!aggScore?.exception,
                linkToException: getExceptionLink({
                  id: aggScore?.exception?.exceptionId || "",
                  queryParams: scope.params,
                }),
                onClick: !!aggScore?.exception
                  ? () => {
                      handleRemoveExceptionDialogOpen({
                        selectedAssets: [
                          {
                            mrn: advisory?.mrn || "",
                            scopeMrn: scope.mrn,
                            findingMrn: advisory?.mrn || "",
                            title: advisory.id,
                            rating: aggScore?.rating || ScoreRating.Unscored,
                            exception: aggScore?.exception,
                          },
                        ],
                      });
                    }
                  : () => {
                      handleSetExceptionDialogOpen({
                        selectedAssets: [
                          {
                            mrn: advisory?.mrn || "",
                            scopeMrn: scope.mrn,
                            findingMrn: advisory?.mrn || "",
                            title: advisory.id,
                            rating: aggScore?.rating || ScoreRating.Unscored,
                            exception: aggScore?.exception || null,
                          },
                        ],
                      });
                    },
                disabled: exceptionsLoading,
              },
            },
            conversation: {
              scope: scope,
              findingMrn: advisory.mrn,
              findingType: "advisoryfinding",
            },
          },
          ...(hasListTicketsPermissions &&
            (tickets?.cases.totalCount || 0 > 0) && {
              ticketsSummary: (
                <TicketsSummary
                  count={tickets?.cases.totalCount || 0}
                  scope={scope}
                />
              ),
            }),
          summary: `**${advisory.title}**\n${advisory.description}`,
          stats,
          right: (
            <ScoreBlock
              hasScore={Boolean(aggScore)}
              mainScore={aggScore?.riskValue}
              cvssScore={cvssScore}
              epssScore={aggScore?.epss}
              blastRadius={aggScore?.blastRadius}
              riskFactors={aggScore?.riskFactors}
              rating={aggScore?.rating || ScoreRating.Unscored}
              hasError={
                Boolean(aggScore) && aggScore?.__typename !== "AggregateScore"
              }
            />
          ),
          left: {
            lower: <Sources id="advisory-sources" links={sourceLinks} />,
          },
          openExceptions: {
            scopeMrn: scope.mrn,
            exceptionId: aggScore?.exception?.exceptionId,
            types: [ExceptionType.Advisory],
          },
        }}
      />
      <InventoryPageHeader>
        <Box id="risk-factors" className="section">
          <SectionHeading heading="Vulnerability insights" />
          <Box className="section-content">
            <CveRiskFactors
              id="cve-risk-factors"
              cvssScore={cvssScore}
              riskFactors={riskFactorsWithDocs}
            />
          </Box>
        </Box>

        <AdvisoryContentDetails advisoryId={advisory.id} scope={scope} />

        <Remediation
          findingId={advisory.id}
          scopeMrn={scope.mrn}
          findingMrn={advisory.mrn}
        />

        <ExposedAssets
          scope={scope}
          contextId={advisory.id}
          filter={{
            mrn: advisory?.mrn,
            types: [FindingType.Advisory],
            state: ScoreStateFilter.All,
          }}
          urlContextType={AssetContextualLinkType.Advisory}
          onCreateTicketClick={handleCreateTicketDialogOpen}
          onCreatePullRequestClick={handleFindingsSecurityPipelinePRDialogOpen}
          onSetExceptionClick={handleSetExceptionDialogOpen}
          onRemoveExceptionClick={handleRemoveExceptionDialogOpen}
          emptyStateMessage="There are currently no exposed assets for this advisory."
          selectionProps={{
            selectedEntities: selectedAffectedAssets,
            hasSelectPermissions: hasCreateTicketPermissions,
            onCheckboxClick: handleNodeClick,
            getIsRowSelected: (rowAssetMrn) => {
              return Boolean(
                selectedAffectedAssets.find(
                  (asset) => asset.mrn === rowAssetMrn,
                ),
              );
            },
            onCheckAll: (checked, edges) => {
              if (checked) {
                setSelectedAffectedAssets(
                  (edges || []).map((edge) => ({
                    groupId: "",
                    scopeMrn: edge?.mrn || "",
                    mrn: edge?.mrn,
                    findingMrn: advisory?.mrn || "",
                    title: advisory?.title || "",
                    exception: null,
                  })),
                );
              } else {
                setSelectedAffectedAssets([]);
              }
            },
            onCheckboxChange: async (
              e: ChangeEvent<HTMLInputElement>,
              checked: boolean,
              asset: ExposedAsset,
            ) => {
              const nextSelected = selectedAffectedAssets.filter(
                (s) => s.mrn !== asset?.assetMrn,
              );
              if (checked) {
                nextSelected.push({
                  mrn: asset?.assetMrn || "",
                  scopeMrn: asset?.assetMrn || "",
                  findingMrn: advisory?.mrn || "",
                  title: advisory?.title || "",
                  exception: asset?.exception || null,
                });
              }

              setSelectedAffectedAssets(nextSelected);
            },
            onCancel: handleCancelClick,
          }}
        />
      </InventoryPageHeader>
    </>
  );
};
